/**
 * TypeScript mirror of `backend/circuits/ir.py` (+ DRC + ImportDiff +
 * CircuitOp + snapshot/mutate/commit/lineage/BOM-merge response shapes).
 *
 * Hand-written rather than codegen-shelling-Python because the IR is
 * small, stable, and a Python codegen step would force every frontend
 * contributor + CI to install Python. Update this file in lockstep
 * with `backend/circuits/ir.py`, `drc.py`, `import_diff.py`, `ops.py`,
 * and `router.py` response models.
 *
 * Identity rule (mirrored from the IR doc): `id` is the stable UUID;
 * `refdes` is a display label, NEVER a key.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Closed enums (string-valued)
// ─────────────────────────────────────────────────────────────────────────────

export type ComponentKind =
  | "resistor"
  | "capacitor"
  | "inductor"
  | "ic"
  | "diode"
  | "mosfet"
  | "bjt"
  | "opamp"
  | "header"
  | "source"
  | "rail"
  | "other";

export const COMPONENT_KINDS: readonly ComponentKind[] = [
  "resistor",
  "capacitor",
  "inductor",
  "ic",
  "diode",
  "mosfet",
  "bjt",
  "opamp",
  "header",
  "source",
  "rail",
  "other",
] as const;

export type PinFunction =
  | "passive"
  | "input"
  | "output"
  | "pwrin"
  | "pwrout"
  | "bidi";

export type SourceFormat =
  | "kicad_sch"
  | "kicad_net"
  | "easyeda"
  | "altium_net"
  | "orcad_net"
  | "edif"
  | "eagle"
  | "librepcb"
  | "ltspice"
  | "spice"
  | "agent"
  | "manual"
  | "legacy_neo4j";

/** Display labels for `SourceFormat` — used in version badges and chips. */
export const SOURCE_FORMAT_LABEL: Record<SourceFormat, string> = {
  kicad_sch: "KiCad schematic",
  kicad_net: "KiCad netlist",
  easyeda: "EasyEDA",
  altium_net: "Altium netlist",
  orcad_net: "OrCAD netlist",
  edif: "EDIF",
  eagle: "Eagle / Fusion",
  librepcb: "LibrePCB",
  ltspice: "LTspice",
  spice: "SPICE",
  agent: "AI-designed",
  manual: "Manual",
  legacy_neo4j: "Legacy import",
};

// ─────────────────────────────────────────────────────────────────────────────
// Source layer
// ─────────────────────────────────────────────────────────────────────────────

export interface Pin {
  number: string;
  name: string | null;
  function: PinFunction;
}

export interface Component {
  id: string;
  refdes: string;
  kind: ComponentKind;
  value: string | null;

  mpn: string | null;
  manufacturer: string | null;
  footprint: string | null;
  description: string | null;
  datasheet_url: string | null;
  package: string | null;
  tolerance: string | null;
  voltage_rating: string | null;
  power_rating: string | null;
  dnp: boolean;
  lifecycle_status: string | null;
  rohs_status: string | null;

  supplier_part_numbers: Record<string, string[]>;
  preferred_supplier: string | null;

  extras: Record<string, string>;

  source_id: string | null;
  ai_label: string | null;
  /** "user" | "import:<fmt>" | "agent:<run_id>" */
  created_by: string;

  parent_module_id: string | null;
  pins: Pin[];

  // ── Cross-cutting role-derivation fields (IR plan §16.5) — all optional ─
  /**
   * Component role inferred by `derive_component_roles` (§16.5):
   * `"DECOUPLING"`, `"PULLUP"`, `"PULLDOWN"`, `"XTAL_LOAD"`. Surfaces
   * via `RolePill` on BomTab passive rows. Optional until the backend
   * threads the role report onto the IR — the pill no-ops when absent.
   */
  role?: "DECOUPLING" | "PULLUP" | "PULLDOWN" | "XTAL_LOAD" | null;
  /** Refdes of the IC the role is owned by, e.g. "U3". */
  role_owner_refdes?: string | null;
  /** Confidence in the role classification — drives the pill's visual. */
  role_confidence?: "high" | "medium" | "low" | null;
}

export interface NetEndpoint {
  component_id: string;
  pin_number: string;
}

export interface Net {
  id: string;
  name: string | null;
  is_power: boolean;
  is_ground: boolean;
  endpoints: NetEndpoint[];
  source_id: string | null;
  /**
   * Origin of `name` — `"user"` for user / EDA-supplied names,
   * `"enriched"` for names derived by `enrich_net_names` (IR plan
   * §16.4), `"anonymous"` for `Net-(C103-Pad1)`-style auto-names that
   * the enrichment pass couldn't improve. Optional until the backend
   * `enrich_net_names` propagates the field onto the IR — the
   * EnrichedNetMarker no-ops when absent.
   */
  name_origin?: "user" | "enriched" | "anonymous" | null;
  /**
   * When `name_origin === "enriched"`, the rule that produced the
   * name (`"power_rail"`, `"bus_protocol_i2c"`, `"single_ic_pin"`,
   * `"connector_fallback"`). Surfaces in the marker tooltip.
   */
  name_rule?: string | null;
  /**
   * Name of the `NetClass` this net belongs to — set by the PCB
   * parser from `(net_class …)` blocks. `null` / absent = inherits
   * the board-wide design rules. Drives colour-by-class in the
   * viewer and per-class clearance halos.
   */
  netclass_name?: string | null;
}

export interface InterfacePort {
  name: string;
  net_id: string;
  direction: "input" | "output" | "bidir" | "passive";
}

export interface Module {
  id: string;
  name: string;
  parameters: Record<string, unknown>;
  component_ids: string[];
  interface_ports: InterfacePort[];
}

export interface CircuitIR {
  id: string;
  name: string;
  circuit_id: string;
  parent_version_id: string | null;
  source_format: SourceFormat;
  components: Component[];
  nets: Net[];
  modules: Module[];
  /**
   * Plugin-sync sidecar warnings (e.g. `multi_sheet_root_only`,
   * `pcb_parse_failed`, `archive_upload_failed`).
   *
   * Backend (`backend/circuits/snapshot_service.py:107`) stashes these
   * on the serialised IR dict so the snapshot review UI can surface
   * them without a schema migration. Pydantic IR doesn't define this
   * field — it's added post-`model_dump` to the JSON payload — but the
   * TypeScript mirror MUST type it so consumers don't reach for
   * `as Record<string, unknown>` escapes (TS2352 in strict mode).
   */
  _plugin_warnings?: string[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Schematic overlay
// ─────────────────────────────────────────────────────────────────────────────

export interface Point {
  x: number;
  y: number;
}

export interface Placement {
  at: [number, number] | null;
  endpoints: [Point, Point] | null;
  direction: "right" | "left" | "up" | "down";
  flip: boolean;
  reverse: boolean;
}

export interface Polyline {
  points: Point[];
}

export interface PortLabel {
  text: string;
  at: [number, number];
}

export interface SchematicOverlay {
  placements: Record<string, Placement>;
  routes: Record<string, Polyline[]>;
  ports: PortLabel[];
}

// ─────────────────────────────────────────────────────────────────────────────
// DRC
// ─────────────────────────────────────────────────────────────────────────────

export type DrcSeverity = "error" | "warning" | "info";

export interface DrcFinding {
  code: string;
  severity: DrcSeverity;
  message: string;
  component_ids: string[];
  net_ids: string[];
}

export interface DrcReport {
  /** The router conflates version_id / snapshot_id into this one field. */
  version_id: string;
  error_count: number;
  warning_count: number;
  info_count: number;
  has_errors: boolean;
  findings: DrcFinding[];
}

// ─────────────────────────────────────────────────────────────────────────────
// ImportDiff (re-import identity heuristic)
// ─────────────────────────────────────────────────────────────────────────────

export interface ImportDiffMatch {
  prior_id: string;
  new_id: string;
  refdes: string;
  confidence: number;
  via: "source_id" | "uuid" | "fingerprint";
}

export interface RefdesChange {
  component_id: string;
  old_refdes: string;
  new_refdes: string;
}

export interface ValueChange {
  component_id: string;
  refdes: string;
  old_value: string | null;
  new_value: string | null;
}

export interface SuggestedRename {
  prior_id: string;
  new_id: string;
  prior_refdes: string;
  new_refdes: string;
  confidence: number;
}

export interface AmbiguousImportMatch {
  prior_id: string;
  prior_refdes: string;
  candidates: SuggestedRename[];
}

/** Mirrors `ImportDiffResponse` in the router. */
export interface ImportDiffResponse {
  from_version_id: string;
  to_version_id: string;
  matched: ImportDiffMatch[];
  added: Component[];
  removed: Component[];
  renamed_refdes: RefdesChange[];
  value_changed: ValueChange[];
  suggested_renames: SuggestedRename[];
  low_confidence: AmbiguousImportMatch[];
}

// ─────────────────────────────────────────────────────────────────────────────
// CircuitOp discriminated union (`backend/circuits/ops.py`)
// ─────────────────────────────────────────────────────────────────────────────

export type ComponentRef = string;
export type NetRef = string;

export interface PinSpec {
  number: string;
  name?: string | null;
  function?: PinFunction;
}

export interface AddComponentOp {
  kind: "add_component";
  handle?: string | null;
  component_kind: ComponentKind;
  value?: string | null;
  refdes?: string;
  ai_label?: string | null;
  mpn?: string | null;
  manufacturer?: string | null;
  footprint?: string | null;
  description?: string | null;
  datasheet_url?: string | null;
  package?: string | null;
  tolerance?: string | null;
  voltage_rating?: string | null;
  power_rating?: string | null;
  dnp?: boolean;
  pins?: PinSpec[];
  extras?: Record<string, string>;
  supplier_part_numbers?: Record<string, string[]>;
}

export interface AddNetOp {
  kind: "add_net";
  handle?: string | null;
  name?: string | null;
  is_power?: boolean;
  is_ground?: boolean;
}

export interface ConnectOp {
  kind: "connect";
  component: ComponentRef;
  pin: string;
  net: NetRef;
  pin_function?: PinFunction | null;
}

export interface DisconnectOp {
  kind: "disconnect";
  component: ComponentRef;
  pin: string;
  net: NetRef;
}

export interface RemoveComponentOp {
  kind: "remove_component";
  component: ComponentRef;
}

export interface RemoveNetOp {
  kind: "remove_net";
  net: NetRef;
}

export type ChangeValueField =
  | "value"
  | "mpn"
  | "footprint"
  | "tolerance"
  | "voltage_rating"
  | "power_rating"
  | "lifecycle_status"
  | "rohs_status"
  | "manufacturer"
  | "description"
  | "datasheet_url"
  | "package"
  | "dnp"
  | "preferred_supplier";

export interface ChangeValueOp {
  kind: "change_value";
  component: ComponentRef;
  field: ChangeValueField;
  new_value: unknown;
}

export interface SetExtraOp {
  kind: "set_extra";
  component: ComponentRef;
  key: string;
  value: string;
}

export interface RenameNetOp {
  kind: "rename_net";
  net: NetRef;
  new_name: string | null;
}

export interface SubstituteMpnOp {
  kind: "substitute_mpn";
  component: ComponentRef;
  new_mpn: string;
  new_manufacturer?: string | null;
  add_supplier_part_numbers?: Record<string, string[]>;
}

export interface ReplaceSubcircuitOp {
  kind: "replace_subcircuit";
  label: string;
  remove?: ComponentRef[];
  add?: AddComponentOp[];
  connect?: ConnectOp[];
}

export type CircuitOp =
  | AddComponentOp
  | AddNetOp
  | ConnectOp
  | DisconnectOp
  | RemoveComponentOp
  | RemoveNetOp
  | ChangeValueOp
  | SetExtraOp
  | RenameNetOp
  | SubstituteMpnOp
  | ReplaceSubcircuitOp;

/** Display labels for op kinds — used in OpListItem. */
export const CIRCUIT_OP_LABEL: Record<CircuitOp["kind"], string> = {
  add_component: "Added component",
  add_net: "Added net",
  connect: "Connected",
  disconnect: "Disconnected",
  remove_component: "Removed component",
  remove_net: "Removed net",
  change_value: "Changed field",
  set_extra: "Set extra field",
  rename_net: "Renamed net",
  substitute_mpn: "Substituted MPN",
  replace_subcircuit: "Replaced subcircuit",
};

// ─────────────────────────────────────────────────────────────────────────────
// Snapshot / mutate / commit / lineage / BOM-merge response shapes
// ─────────────────────────────────────────────────────────────────────────────

export interface SnapshotResponse {
  snapshot_id: string;
  session_id: string;
  parent_id: string | null;
  content_hash: string;
  ir: CircuitIR;
}

export interface MutateResponse {
  snapshot_id: string;
  parent_snapshot_id: string;
  ir: CircuitIR;
  handle_to_component_id: Record<string, string>;
  handle_to_net_id: Record<string, string>;
}

export interface CommitResponse {
  version_id: string;
  version_number: number;
  semver_label: string | null;
  content_hash: string;
  component_count: number;
  net_count: number;
}

/** Shape the backend returns inside a 422 from `commit_snapshot`. */
export interface CommitGateErrorDetail {
  message: string;
  drc_report: {
    error_count: number;
    warning_count: number;
    info_count: number;
    findings: DrcFinding[];
  };
}

export interface BomMergeConflict {
  refdes: string;
  field: string;
  ir_value: unknown;
  bom_value: unknown;
  /** What we kept after applying BOM-vs-schematic precedence rules. */
  resolved: unknown;
}

export interface BomMergeResponse {
  snapshot_id: string;
  parent_snapshot_id: string;
  ir: CircuitIR;
  matched: string[];
  bom_only: string[];
  sch_only: string[];
  conflicts: BomMergeConflict[];
  duplicate_refdes_in_bom: string[];
  /** CSV column → IR field mapping the parser inferred / was given. */
  column_mapping: Record<string, string>;
}

export interface LineageEntry {
  snapshot_id: string;
  parent_id: string | null;
  content_hash: string;
  /** ISO-8601 string. */
  created_at: string;
}

export interface LineageResponse {
  snapshot_id: string;
  /** Newest-first: `[start, parent, grandparent, ...]`. */
  chain: LineageEntry[];
}

export interface IrResponse {
  version_id: string;
  ir: CircuitIR;
}

// ─────────────────────────────────────────────────────────────────────────────
// PCB overlay (IR plan §2 Layer 3, Phase 16)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A copper pad on a footprint — through-hole or SMD. Coordinates are
 * footprint-local; the parent `<g>`'s translate/rotate brings them into
 * world space. Mirrors the backend `Pad` Pydantic model.
 */
export interface Pad {
  number: string;
  /** Pad centre, footprint-local mm. */
  at: [number, number];
  /** Pad size in mm. For circles, X == Y == diameter. */
  size: [number, number];
  shape: "circle" | "rect" | "oval" | "roundrect" | "trapezoid";
  /**
   * `connect` is KiCad's edge-connector pad (no drill, no mask).
   * `np_thru_hole` is a non-plated mounting hole.
   */
  type: "smd" | "thru_hole" | "np_thru_hole" | "connect";
  /** Raw KiCad layer tokens — `["F.Cu","F.Mask"]` for SMD on top,
   *  `["*.Cu","*.Mask"]` for through-hole. */
  layers: string[];
  /** Drill diameter in mm. `null` for SMD. */
  drill: number | null;
  /** Local rotation in degrees relative to the footprint. */
  rotation: number;
  /** Net name from the .kicad_pcb's net table — used for net-cascade
   *  hover highlight on the same channel as `PCBTrack.net_id`. */
  net_name: string | null;
}

/**
 * A non-copper graphic primitive belonging to a footprint —
 * courtyard outline, fab marking, silkscreen line / circle / arc /
 * text. Coordinates are footprint-local.
 */
export interface FootprintGraphic {
  kind: "line" | "circle" | "arc" | "text" | "polygon";
  /** Raw KiCad layer token (F.CrtYd, F.SilkS, F.Fab, B.SilkS, …). */
  layer: string;
  /** Stroke width in mm. */
  width: number;
  // Line/arc/text positioning. `start` doubles as the text anchor.
  start?: [number, number] | null;
  end?: [number, number] | null;
  // Circle-only fields:
  center?: [number, number] | null;
  radius?: number | null;
  // Arc-only field — the midpoint of the arc, KiCad's 3-point form.
  arc_mid?: [number, number] | null;
  // Text-only fields:
  text?: string | null;
  text_size?: [number, number] | null;
  rotation?: number;
  // Polygon-only field — closed-vertex list.
  polygon?: [number, number][] | null;
}

/**
 * One footprint's placement on the PCB. `at` is in millimetres,
 * KiCad-convention origin (top-left). `rotation` is degrees CCW.
 * `layer` is the minimal F.Cu / B.Cu pair; `flipped: true` mirrors
 * the footprint about its placement origin (used for back-side parts).
 */
export interface PCBPlacement {
  at: [number, number];
  rotation: number;
  layer: "F.Cu" | "B.Cu";
  flipped: boolean;
  /** Per-pad copper geometry. Empty array = no pad data — renderer
   *  falls back to a kind-keyed bounding rect. */
  pads?: Pad[];
  /** Courtyard / silk / fab line + circle primitives. Empty array =
   *  no footprint outline; the renderer skips that layer. */
  graphics?: FootprintGraphic[];
}

/**
 * One polyline track segment on a copper layer carrying a specific
 * net's signal. `points` is a list of (x, y) vertices in mm; the
 * track is rendered as a polyline of `width` mm.
 */
export interface PCBTrack {
  /** Stable backend-minted UUID — drives React keys, DRC marker
   *  correlation across re-renders, and (future) per-track undo. */
  id: string;
  net_id: string;
  points: [number, number][];
  /** Track width in mm. Default 0.25mm matches KiCad's `Default` netclass. */
  width: number;
  layer: string;
}

/**
 * A copper via — through-hole / buried / blind, connecting nets across
 * copper layers. Rendered as a copper annulus with a drill aperture.
 */
export interface Via {
  /** Stable backend-minted UUID — same purpose as `PCBTrack.id`. */
  id: string;
  net_id: string;
  /** Centre, board-level mm. */
  at: [number, number];
  /** Annulus diameter in mm. */
  size: number;
  /** Drill diameter in mm. Always less than `size`. */
  drill: number;
  /** Pair of copper layers connected — `["F.Cu","B.Cu"]` for through. */
  layers: [string, string];
}

/**
 * A board-level graphic primitive — board outline (Edge.Cuts) or free
 * silk / fab line / circle / arc / text. Coordinates are in board
 * frame, not footprint-local. Mirrors the backend `BoardGraphic`
 * Pydantic model.
 */
export interface BoardGraphic {
  kind: "line" | "circle" | "arc" | "text" | "polygon";
  /** Raw KiCad layer token (Edge.Cuts, F.SilkS, B.Fab, …). */
  layer: string;
  /** Stroke width in mm. */
  width: number;
  start?: [number, number] | null;
  end?: [number, number] | null;
  center?: [number, number] | null;
  radius?: number | null;
  arc_mid?: [number, number] | null;
  text?: string | null;
  text_size?: [number, number] | null;
  rotation?: number;
  /** Closed-polygon vertex list for `kind="polygon"`. */
  polygon?: [number, number][] | null;
}

/** A copper-fill region. Renderer paints it as a filled polygon under
 *  the tracks layer at low opacity. Net hover binds via `net_id`. */
export interface Zone {
  net_id: string;
  layer: string;
  /** Filled polygon outline in mm. KiCad-emitted, with keepouts
   *  already subtracted around pads / tracks. */
  polygon: [number, number][];
  priority: number;
}

/**
 * Cross-CAD-normalised layer classification — mirror of
 * `backend/circuits/ir.py::LayerKind`. The render pipeline + DRC
 * dispatch on this enum, never on the raw `name` string. Adapters
 * populate it once at parse time so KiCad / EasyEDA / Altium /
 * EDIF imports all flow through the same renderer code.
 */
export type LayerKind =
  | "copper_top"
  | "copper_bottom"
  | "copper_inner"
  | "silk_top"
  | "silk_bottom"
  | "mask_top"
  | "mask_bottom"
  | "paste_top"
  | "paste_bottom"
  | "fab_top"
  | "fab_bottom"
  | "courtyard_top"
  | "courtyard_bottom"
  | "edge_cuts"
  | "drill"
  | "user"
  | "unknown";

/** Closed list — drives renderer fallbacks and the LayerControl
 *  ordering. Order here = stack order from top to bottom. */
export const LAYER_KIND_VALUES: readonly LayerKind[] = [
  "copper_top",
  "copper_inner",
  "copper_bottom",
  "silk_top",
  "silk_bottom",
  "mask_top",
  "mask_bottom",
  "paste_top",
  "paste_bottom",
  "fab_top",
  "fab_bottom",
  "courtyard_top",
  "courtyard_bottom",
  "edge_cuts",
  "drill",
  "user",
  "unknown",
] as const;

/** Stackup entry — name + role + normalised kind. Full stackup
 *  (thicknesses, dielectrics) deferred to V2. */
export interface PCBLayer {
  name: string;
  type: "copper" | "soldermask" | "silkscreen" | "paste" | "edge_cuts" | "user";
  /** Cross-CAD normalisation. Defaults to "unknown" so legacy overlays
   *  saved before LayerKind landed don't crash the renderer. */
  kind?: LayerKind;
  /** 1-based inner copper index when kind === "copper_inner". */
  inner_index?: number | null;
}

/**
 * Board-wide DRC rules — clearance, minimum track width, via
 * geometry. Drives the clearance-halo gesture and DRC live-preview
 * overlays. All fields optional with conservative defaults; a `null`
 * field means "the source didn't ship a value — viewer can't draw
 * the corresponding overlay until the user fills it in."
 *
 * Per-netclass overrides live on `NetClass`; this carries the board
 * defaults that apply when a net has no class.
 */
export interface DesignRules {
  min_track_width_mm?: number | null;
  min_clearance_mm?: number | null;
  min_via_diameter_mm?: number | null;
  min_via_drill_mm?: number | null;
  min_annular_ring_mm?: number | null;
  min_hole_to_hole_mm?: number | null;
}

/**
 * A named group of nets sharing electrical / fab rules. Drives
 * colour-by-class in the viewer + per-class clearance halos. Each
 * field overrides the matching board-wide design_rules entry; `null`
 * = inherit.
 */
export interface NetClass {
  name: string;
  description?: string | null;
  track_width_mm?: number | null;
  clearance_mm?: number | null;
  via_diameter_mm?: number | null;
  via_drill_mm?: number | null;
  diff_pair_gap_mm?: number | null;
  diff_pair_width_mm?: number | null;
}

/**
 * Optional PCB geometry layered onto a CircuitIR. Mirrors the backend
 * `PCBOverlay` Pydantic model. Empty arrays are valid — caller assumes
 * the KiCad-default 2-layer F.Cu / B.Cu stack when `layers` is empty.
 */
export interface PCBOverlay {
  /** component_id → footprint placement on the PCB. */
  placements: Record<string, PCBPlacement>;
  /** Copper segments. Render order is array-order. */
  tracks: PCBTrack[];
  layers: PCBLayer[];
  /** Through-hole / buried / blind vias. Empty for legacy overlays
   *  saved before via support. */
  vias?: Via[];
  /** Board-level lines + circles on Edge.Cuts / silk / fab. Drives
   *  the board outline + free silkscreen. Empty for legacy overlays. */
  board_graphics?: BoardGraphic[];
  /** Copper-fill regions (e.g. ground pours). Empty for legacy or
   *  zoneless overlays. */
  zones?: Zone[];
  /** Board-wide DRC rules. `null` / absent when the source didn't
   *  ship rules data (legacy imports, schematic-only formats). */
  design_rules?: DesignRules | null;
  /** Net-class definitions in priority order — first-match wins
   *  when a net belongs to several. Empty / absent = no class
   *  metadata; every net falls back to `design_rules`. */
  netclasses?: NetClass[];
}

/**
 * Importance tier — drives the canvas density slider. `key` is the
 * default-on tier; `normal` reveals on the second click; `minor`
 * shows only when the user explicitly asks for full coverage.
 */
export type AnnotationImportance = "key" | "normal" | "minor";

/**
 * One floating callout on the PCB canvas. Mirror of
 * `backend/circuits/annotations.py::BoardAnnotation`. The shape is
 * generator-agnostic — v0 (deterministic heuristics) and the future
 * v1 (LLM-driven agent) emit identical JSON, so the canvas surface
 * doesn't change as the generator gets smarter.
 */
export interface BoardAnnotation {
  /** Component this annotation points at. The canvas joins against
   *  PCBOverlay.placements to resolve x/y. */
  component_id: string;
  /** Cached so chips render without an IR join on every paint. */
  refdes: string;
  /** Headline rendered in the chip body. */
  role: string;
  /** Markdown paragraph the detail panel renders. May be empty. */
  narrative_md?: string;
  /** Density tier — see `AnnotationImportance`. */
  importance: AnnotationImportance;
  /** Optional list of named nets the part participates in. */
  nets?: string[];
  /** Tag for the generator that produced this annotation
   *  (`v0_deterministic`, future `v1_agent`, …). Helps the UI show
   *  "generated by …" provenance and selectively invalidate caches. */
  generator?: string;
}

export interface BoardAnnotationSet {
  version_id: string;
  annotations: BoardAnnotation[];
  generator: string;
}

/**
 * Functional-blocks payload for the schematic page's "Blocks" view.
 * Mirrors `backend.circuits.schemas.BlocksResponse`. The inner
 * `payload` shape is owned by `schematic_tools.build_functional_
 * blocks_payload`; consumers read it through the `BlocksPayload`
 * type below.
 */
export interface BlocksResponse {
  version_id: string;
  /** Null when the board has no connected components (early upload,
   *  every net stripped as power, etc.) — the canvas renders a
   *  "no blocks yet" empty state. */
  payload: BlocksPayload | null;
}

/** v1 of the blocks payload — Louvain partition + thresholded
 *  inter-block edges. */
export interface BlocksPayload {
  schema_version: 1;
  project_name: string;
  blocks: BlockEntry[];
  edges: BlockEdge[];
}

export interface BlockEntry {
  /** Stable id within this payload — `block_1`, `block_2`, ...
   *  Matches the order Louvain emits + the size-sort detect_*()
   *  applies; opaque to consumers. */
  id: string;
  label: string;
  /** Inferred role string (e.g. "Power Supply", "MCU", "Sensor")
   *  or null when the heuristic couldn't classify. */
  role: string | null;
  count: number;
  ic_count: number;
  buses: string[];
  /** Refdes only — kept tight to keep the payload small. The full
   *  member metadata lives in the schematic IR if a deeper view
   *  becomes necessary. */
  members: string[];
}

export interface BlockEdge {
  source_block_id: string;
  target_block_id: string;
  /** Number of distinct nets connecting these two blocks. The
   *  backend thresholds at >=2; single-net "edges" are stripped
   *  to keep the canvas readable. */
  net_count: number;
  /** Up to 5 representative net names — surfaced in the drawer
   *  / tooltip so the engineer can see what the bundle is. */
  sample_nets: string[];
}

/**
 * One DRC finding rendered on the canvas. Same shape as `DrcFinding`
 * on the backend, plus an optional `at` board-mm coordinate the
 * `/drc` endpoint computes from the PCB overlay.
 */
export interface SpatialDrcFinding {
  code: string;
  severity: "error" | "warning" | "info";
  message: string;
  component_ids?: string[];
  net_ids?: string[];
  /** Board-mm centroid. `null` when no spatial anchor was derivable
   *  (no overlay, or finding references parts the overlay didn't
   *  place). The list-view still surfaces the finding; the canvas
   *  marker layer skips it. */
  at: [number, number] | null;
}

export interface PCBOverlayResponse {
  version_id: string;
  /** `null` when the version has no `.kicad_pcb` (schematic-only flow). */
  overlay: PCBOverlay | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Multi-Circuit lifecycle (IR plan §8.3 + Phase 14 endpoints)
// ─────────────────────────────────────────────────────────────────────────────

export type CircuitKind = "main" | "candidate" | "fixture" | "scratch";
export type CircuitStatus =
  | "ephemeral"
  | "draft"
  | "active"
  | "released"
  | "archived";

export interface CircuitDTO {
  id: string;
  project_id: string;
  name: string;
  kind: CircuitKind;
  status: CircuitStatus;
  parent_circuit_id: string | null;
  /** Free-text markdown describing the circuit's intent (why it
   *  exists, what trade-offs it's exploring). Distinct from
   *  per-version descriptions. Null when never filled in. */
  description: string | null;
  /** Markdown release notes captured at the moment the circuit
   *  transitioned to RELEASED. Null when never released, or
   *  released without notes. */
  release_notes_md: string | null;
  created_at: string;
  last_accessed_at: string | null;
  promoted_at: string | null;
  archived_at: string | null;
  current_version_id: string | null;
  current_version_number: number | null;
  current_version_semver: string | null;
  /** Counts cached on the keeper for fast UI rendering. */
  component_count?: number;
  net_count?: number;
}

export interface CircuitVersionRow {
  id: string;
  version_number: number;
  semver_label: string | null;
  uploaded_at: string;
  is_current: boolean;
  imported: boolean;
  source_format: string | null;
  component_count: number;
  net_count: number;
  description: string | null;
}

export interface BranchCircuitRequest {
  name: string;
  description?: string;
  source_version_id?: string;
}

export interface BranchCircuitResponse {
  new_circuit: CircuitDTO;
  version_id: string;
}

export interface MergeCircuitMapping {
  /** "snubber.IN" — port name in the source circuit (the candidate). */
  source_port: string;
  /** "U7.drain" — refdes.pin in the target circuit (the main board). */
  target_pin: string;
}

export interface MergeCircuitRequest {
  source_circuit_id: string;
  mapping: MergeCircuitMapping[];
  /** When true the source circuit is archived after the merge. */
  archive_source?: boolean;
  description?: string;
}

export interface MergeCircuitResponse {
  target_circuit_id: string;
  source_circuit_id: string;
  version_id: string;
  /** "R?1 → R48" mapping the annotation pass produced. */
  refdes_remap: Record<string, string>;
}

export interface PromoteCircuitRequest {
  /** When set, transition straight to ACTIVE; otherwise lands in DRAFT. */
  publish?: boolean;
  description?: string;
}

export interface PromoteCircuitResponse {
  circuit_id: string;
  status: CircuitStatus;
  promoted_at: string;
  version_id: string;
}

export interface PatchCircuitRequest {
  name?: string;
  status?: CircuitStatus;
  /** Markdown intent description. Empty string clears the field;
   *  omit to leave unchanged. */
  description?: string;
  /** Markdown release notes — typically supplied alongside
   *  `status: "released"`. Empty string clears; omit to leave
   *  unchanged. */
  release_notes_md?: string;
}
